import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import TablePaginationBar from '../../../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../../../hooks';
import HelpIcon from '../../../../../components/help/HelpIcon';

const WorkspaceModelsDialog = ({
  open,
  workspace,
  allowManage,
  onClose,
  fetchModels,
  addModel,
  removeModel
}) => {
  const [models, setModels] = useState([]);
  const [pendingModelId, setPendingModelId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const { pageRows, paginationProps } = useTablePagination(models, {
    storageKey: 'workspaceModels',
    // el diálogo se reabre por workspace: al abrirlo, siempre página 1
    resetDeps: [open]
  });

  useEffect(() => {
    if (!open || !workspace) {
      setModels([]);
      setPendingModelId('');
      setError(null);
      return;
    }

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchModels?.(workspace);
        setModels(response || []);
      } catch (err) {
        console.error('WorkspaceModelsDialog.load: error al obtener modelos vinculados', err);
        setError(err.message || 'No fue posible cargar los modelos vinculados.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [open, workspace, fetchModels]);

  const handleAddModel = async () => {
    if (!pendingModelId.trim()) {
      setError('Introduce el identificador del modelo que deseas vincular.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const updated = await addModel?.(workspace, pendingModelId.trim());
      setModels(updated || []);
      setPendingModelId('');
    } catch (err) {
      console.error('WorkspaceModelsDialog.handleAddModel: error', err);
      setError(err.message || 'No fue posible agregar el modelo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveModel = async (modelId) => {
    try {
      setIsLoading(true);
      setError(null);
      const updated = await removeModel?.(workspace, modelId);
      setModels(updated || []);
    } catch (err) {
      console.error('WorkspaceModelsDialog.handleRemoveModel: error', err);
      setError(err.message || 'No fue posible eliminar el modelo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--wb-bg-surface)',
          border: '1px solid var(--wb-border-16)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--wb-text-title)' }}>Modelos incluidos</DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'var(--wb-border)' }}>
        <Paper
          elevation={0}
          sx={{
            bgcolor: 'var(--wb-bg-card)',
            p: 2,
            borderRadius: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AnnouncementIcon sx={{ color: 'var(--wb-primary)' }} />
          </Box>
          <Typography variant="body2" color="var(--wb-text-strong)">
            En él siguiente listado se muestran los modelos incluidos en el Espacio de Análisis.
            Esto permite al Agente LLM poder tener acceso a los modelos incluidos en este apartado con la finalidad de hacer cruce y analisis de información.
            Puedes agregar o modificar él listado en la opción modificar del listado principal.
          </Typography>
        </Paper>

        {allowManage && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              label="Identificador del modelo semántico"
              placeholder="Ej. sm_324fa9971fa64d8baf90c0835c6de58d"
              fullWidth
              value={pendingModelId}
              onChange={(event) => setPendingModelId(event.target.value)}
              disabled={isLoading}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiInputBase-root': {
                  color: 'var(--wb-text-title)',
                  backgroundColor: 'var(--wb-bg-overlay-90)'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--wb-border-35)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--wb-blue)'
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--wb-muted-80)'
                }
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddModel}
              disabled={isLoading}
              sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' }, whiteSpace: 'nowrap' }}
            >
              Agregar
            </Button>
            <HelpIcon module="modelo-semantico" topic="espacio-modelos" anchor="identificador-del-modelo-semántico" />
          </Box>
        )}

        {error && (
          <Typography variant="body2" sx={{ color: 'var(--wb-danger)', mb: 2 }}>
            {error}
          </Typography>
        )}

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            backgroundColor: 'var(--wb-bg-panel)',
            borderRadius: 2,
            border: '1px solid var(--wb-border)'
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'var(--wb-muted-80)', fontWeight: 600 }}>Nombre</TableCell>
                <TableCell sx={{ color: 'var(--wb-muted-80)', fontWeight: 600 }}>Descripción</TableCell>
                <TableCell sx={{ color: 'var(--wb-muted-80)', fontWeight: 600 }}>Dominio</TableCell>
                <TableCell sx={{ color: 'var(--wb-muted-80)', fontWeight: 600 }}>Versión</TableCell>
                {allowManage && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && models.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={allowManage ? 5 : 4}>
                    <Typography sx={{ color: 'var(--wb-muted-80)', textAlign: 'center', py: 3 }}>
                      Cargando modelos vinculados…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : models.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={allowManage ? 5 : 4}>
                    <Typography sx={{ color: 'var(--wb-muted-80)', textAlign: 'center', py: 3 }}>
                      No se encontraron modelos vinculados al espacio.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((model) => (
                  <TableRow key={model.semantic_model_id || model.semanticModelId || model.name}>
                    <TableCell sx={{ color: 'var(--wb-text-title)' }}>{model.name || model.semantic_model_id}</TableCell>
                    <TableCell sx={{ color: 'var(--wb-muted-80)' }}>
                      {model.description || 'Sin descripción'}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--wb-text-title)' }}>
                      {model.domain || model.domain_name || '—'}
                    </TableCell>
                    <TableCell sx={{ color: 'var(--wb-text-title)' }}>
                      {model.version || model.version_id || model.versionId || '—'}
                    </TableCell>
                    {allowManage && (
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveModel(model.semantic_model_id || model.semanticModelId || model.id)}
                          disabled={isLoading}
                          sx={{ color: 'var(--wb-danger)' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {models.length > 0 && <TablePaginationBar {...paginationProps} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isLoading} sx={{ color: 'var(--wb-muted)' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WorkspaceModelsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  workspace: PropTypes.object,
  allowManage: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  fetchModels: PropTypes.func,
  addModel: PropTypes.func,
  removeModel: PropTypes.func
};

WorkspaceModelsDialog.defaultProps = {
  workspace: null,
  allowManage: false,
  fetchModels: undefined,
  addModel: undefined,
  removeModel: undefined
};

export default WorkspaceModelsDialog;
